import { WarpigsLogo } from "@/logos/WarpigsLogo";
import { createContext, useEffect } from "react";
import { locationRoute } from "@/App";
import { MikkellerAndFriendsLogo } from "@/logos/MikkellerAndFriendsLogo";
import { CaminoLogo } from "@/logos/CaminoLogo";
import { HookedLogo } from "@/logos/HookedLogo";
import FaergekroenLogo from "@/logos/FaergekroenLogo";
import {
  caminoTheme,
  defaultTheme,
  faergekroenTheme,
  mikkellerAndFriendsTheme,
  hookedTheme,
  Theme,
  //warpigsTheme,
  rtbTheme,
  frihedenTheme,
  psBarCityTheme,
  motherCityTheme,
  seasideTheme,
  mikkellerBaghavenTheme,
  bootleggersTheme,
  cocksAndCowsTheme,
  copenhellTheme,
} from "@/lib/themes";

import { RamenToBiiruLogo } from "@/logos/RamenToBiiruLogo";
import { FrihedenLogo } from "@/logos/FrihedenLogo";
import { PsBarCityLogo } from "@/logos/PsBarCityLogo";
import { MotherCityLogo } from "@/logos/MotherCityLogo";
import { SeasideLogo } from "@/logos/SeasideLogo";
import { MikkellerBaghavenLogo } from "@/logos/MikkellerBaghavenLogo";
import { BootleggersLogo } from "@/logos/BootleggersLogo";
import { CocksAndCowsLogo } from "@/logos/CocksAndCowsLogo";
import { CopenhellLogo } from "@/logos/CopenhellLogo";
import { Copenhell26Logo } from "@/logos/Copenhell26Logo";

type CategoryLayout = "GRID" | "LIST";
type MenuLayout = "GRID" | "LIST" | "SWIPEABLE";

export type BusyLevel = "LOW" | "MEDIUM" | "HIGH" | "VERY_HIGH";

export type LocationDialog = {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  showOn:
    | "firstVisit"
    | "always"
    | "beforeCheckout"
    | "busy-MEDIUM"
    | "busy-HIGH"
    | "busy-VERY_HIGH";
  canCancel: boolean;
  canClose: boolean;
};

export type UpsaleConfig = {
  name: string;
  categoryIds: string[];
  categoryToUpsellId: string;
};

export type RebateConfig = {
  categoryId: string;
  setSize: number;
  discountPerSet: number;
};

type LocationConfiguration = {
  theme: Theme;
  menuLayout: MenuLayout;
  categoryLayout: CategoryLayout;
  logo?: React.ReactNode;
  showTitle?: boolean;
  showTableSelector: boolean;
  lockTableSelector: boolean;
  takeAwayEnabled?: boolean;
  headingTypography: "sans-serif" | "serif" | "cheddar";
  newsletterSignUp?: {
    provider: "klaviyo";
    enabled: boolean;
    config: {
      locationTitle: string;
      companyId: string;
      listId: string;
      signupMessage: string;
    };
  };
  enablePhoneNumberInCheckout: boolean;
  enableNameInCheckout: boolean;
  dialogs?: LocationDialog[];
  hasBusyIndicator?: boolean;
  upsales?: UpsaleConfig[];
  contact?: {
    address: string;
    phone: string;
  };
  useShortOrderNumber?: boolean;
  rebate?: RebateConfig;
};

type LocationName =
  | "default"
  | "warpigs"
  | "mikkellerAndFriends"
  | "faergekroen"
  | "ramenToBiiru"
  | "camino"
  | "hooked"
  | "friheden"
  | "ps-bar-city"
  | "mother-city"
  | "seaside"
  | "mikkeller-baghaven"
  | "bootleggers"
  | "cocks-and-cows"
  | "copenhell"
  | "copenhell-s2";

// Map that makes it a bit more descriptive to work with locations w/ names rather than UUID's
const locationNames: Record<string, LocationName> = {
  "35308d55-4db8-45a1-9d6a-88242e819481": "warpigs",
  //"CAMINO-ID": "camino", // TODO
  "e33fa7cd-4821-4bc0-86a5-b2ded054a86a": "faergekroen",
  "816834a5-0962-44a9-8333-81c3cb115fb1": "mikkellerAndFriends",
  "49c3e238-9ec9-4bbd-a848-c738f8be2eba": "hooked", // Hooked
  "cab60d0f-0dfd-488e-a0d5-82717c897a73": "ramenToBiiru", // RTB Vesterbro
  "477acf4e-a6e1-48c5-a710-1e02bf2ad1ff": "ramenToBiiru", // RTB Nørrebro
  "4c58959f-aaf7-485c-ac73-3532f98903c3": "ramenToBiiru", // RTB Frederiksberg
  "a7b52dc3-905a-45c0-9daa-82550b5da187": "ramenToBiiru", // RTB Valby
  "413c5590-60e8-4505-93bd-809f528e235f": "ramenToBiiru", // RTB Østerbro
  "f2f584f8-994a-424d-8d32-7aa91536f4a6": "ramenToBiiru", // RTB Strandvejen
  "9a6f51d3-9f26-4d38-afab-db079a333bdd": "friheden",
  "d7264308-f171-4994-9ccc-7250e2ab87b5": "ps-bar-city", // ps-bar-city
  "c2a0963f-5530-4ffd-9c53-3ba27c16771d": "mother-city", // mother-city (DEV)
  "bedf0a1b-210d-46ca-b0ca-686546d8bc4b": "mother-city", // mother-city (PROD)
  "abf1e4e1-4448-4c98-9749-e6de89ccd93d": "seaside",
  "9d8852b1-64c5-4877-8529-aa19ab1e9701": "mikkeller-baghaven",
  "5267795b-ae8a-4623-a87f-375c6b61e757": "bootleggers",
  "b49aa969-6162-4140-a083-602990713013": "cocks-and-cows",
  "d7418af9-56ca-4f0b-89f8-1169856142e0": "copenhell", // RIP
  "f3e6712b-6eb5-4d10-bcc1-f9f7ad72d4f7": "copenhell-s2", // S-2 Hallen
};

const defaultConfiguration: LocationConfiguration = {
  theme: defaultTheme,
  showTitle: false,
  categoryLayout: "LIST",
  menuLayout: "LIST",
  showTableSelector: true,
  lockTableSelector: false,
  takeAwayEnabled: false,
  headingTypography: "sans-serif",
  enablePhoneNumberInCheckout: false,
  enableNameInCheckout: false,
  dialogs: [],
  hasBusyIndicator: false,
  upsales: [],
  contact: {
    address: "",
    phone: "",
  },
  useShortOrderNumber: false,
};

const copenhellConfiguration: LocationConfiguration = {
  ...defaultConfiguration,
  logo: <CopenhellLogo />,
  theme: copenhellTheme,
  headingTypography: "cheddar",
  dialogs: [
    {
      title: "Bemærk!",
      description: `
        Ved brug af TableTaxi kan du ikke bytte emballage, men betaler emballageafgift på hver købt drikkevare.`,
      confirmText: "OK",
      showOn: "firstVisit",
      canCancel: false,
      canClose: true,
    },
  ],
  rebate: {
    categoryId: "a99ac23f-28aa-4580-bdbc-89695a2a4e20",
    setSize: 4,
    discountPerSet: 40,
  },
};

/*
const defaultMediumBusyDialog: LocationDialog = {
  title: "Please note",
  description: `
  We are currently busy, and your order might take a while to reach you.

  Thank you for your patience!`,
  confirmText: "Got it",
  canClose: true,
  canCancel: false,
  showOn: "busy-MEDIUM",
};

const defaultBusyDialog: LocationDialog = {
  title: "Please note",
  description: `
  We are currently quite busy, and wont be able to take orders for a while.

  Thank you for your patience!`,
  canClose: false,
  canCancel: false,
  showOn: "busy-HIGH",
};

const defaultVeryBusyDialog: LocationDialog = {
  title: "Please note",
  description: `
  We are currently very busy, and wont be able to take orders for a while.

  Thank you for your patience!`,
  canClose: false,
  canCancel: false,
  showOn: "busy-VERY_HIGH",
};*/

const configurations: Record<LocationName, LocationConfiguration> = {
  default: defaultConfiguration,
  warpigs: {
    ...defaultConfiguration,
    logo: <WarpigsLogo />,
    //headingTypography: "serif",

    //menuLayout: "SWIPEABLE",
    //categoryLayout: "LIST",

    //headingTypography: "serif",

    contact: {
      address: "Flæsketorvet 25-37, 1711 København V",
      phone: "+45 43 48 48 48",
    },

    takeAwayEnabled: true,
    useShortOrderNumber: true,

    newsletterSignUp: {
      provider: "klaviyo",
      enabled: true,
      config: {
        listId: "SDASRU",
        companyId: "NKbZGi",
        locationTitle: "Warpigs",
        signupMessage:
          "Sign up for a chance to win a full night of free beers. Winner will be instantly notified 🍻",
      },
    },
    dialogs: [
      //defaultMediumBusyDialog,
      //defaultBusyDialog,
      //defaultVeryBusyDialog,
    ],
    hasBusyIndicator: true,
    upsales: [
      {
        name: "Upsell beer with snacks",
        categoryIds: ["bd025270-07d2-472c-a83a-3f279c6b96cb"], // Snacks
        categoryToUpsellId: "faa90a6a-ebb2-449c-8ff1-339555bb65d0", // Beers
      },
      {
        name: "Upsell snacks with beer",
        categoryIds: [
          "faa90a6a-ebb2-449c-8ff1-339555bb65d0",
          "3ae860de-7ab8-4525-99fd-2c6da25cb2e7",
        ], // Beers & Beer 2L Pitcher
        categoryToUpsellId: "bd025270-07d2-472c-a83a-3f279c6b96cb", // Snacks
      },
    ],
  },
  hooked: {
    ...defaultConfiguration,
    theme: hookedTheme,
    logo: <HookedLogo />,
  },
  seaside: {
    ...defaultConfiguration,
    theme: seasideTheme,
    logo: <SeasideLogo />,
    enablePhoneNumberInCheckout: true,
  },
  faergekroen: {
    ...defaultConfiguration,
    theme: faergekroenTheme,
    logo: <FaergekroenLogo />,
    newsletterSignUp: {
      provider: "klaviyo",
      enabled: true,
      config: {
        listId: "SDASRU",
        companyId: "NKbZGi",
        locationTitle: "Færgekroen",
        signupMessage:
          "Sign up to the newsletter and stay up-to-date with the latest and greatest from the Mikkeller universe",
      },
    },
  },
  mikkellerAndFriends: {
    ...defaultConfiguration,
    theme: mikkellerAndFriendsTheme,
    logo: <MikkellerAndFriendsLogo />,
    hasBusyIndicator: true,
    upsales: [
      {
        name: "Suggest snacks if buying anything not snacks",
        categoryIds: ["*"],
        categoryToUpsellId: "55c385a1-2430-460e-b9eb-14c8d5f283ed", // Snacks
      },
    ],
  },
  camino: {
    ...defaultConfiguration,
    theme: caminoTheme,
    categoryLayout: "GRID",
    menuLayout: "LIST",
    logo: <CaminoLogo />,
    lockTableSelector: true,
  },
  ramenToBiiru: {
    ...defaultConfiguration,
    theme: rtbTheme,
    logo: <RamenToBiiruLogo />,
    showTableSelector: false,
    showTitle: true,
  },
  friheden: {
    ...defaultConfiguration,
    theme: frihedenTheme,
    logo: <FrihedenLogo />,
    dialogs: [
      {
        title: "Please note",
        description: `
        Be aware, that you can only order items for the table that you are sitting at, it is not allowed to order for tables without QR codes.
        
        Please do not move our tables or chairs!

        Thank you ,
        - Love Friheden -`,
        confirmText: "Got it",
        showOn: "firstVisit",
        canCancel: false,
        canClose: true,
      },
    ],
  },
  "ps-bar-city": {
    ...defaultConfiguration,
    theme: psBarCityTheme,
    logo: <PsBarCityLogo />,
  },
  "mother-city": {
    ...defaultConfiguration,
    theme: motherCityTheme,
    logo: <MotherCityLogo />,
    contact: {
      address: "Ny Østergade 14, 1101 København K",
      phone: "+45 22 27 58 98",
    },
  },
  "mikkeller-baghaven": {
    ...defaultConfiguration,
    theme: mikkellerBaghavenTheme,
    logo: <MikkellerBaghavenLogo />,
    newsletterSignUp: {
      provider: "klaviyo",
      enabled: true,
      config: {
        listId: "SDASRU",
        companyId: "NKbZGi",
        locationTitle: "Baghaven",
        signupMessage:
          "Sign up to the newsletter and stay up-to-date with the latest and greatest from the Mikkeller universe",
      },
    },
    upsales: [
      {
        name: "Suggest snacks if buying anything not snacks",
        categoryIds: ["*"],
        categoryToUpsellId: "8945efea-c15d-4e03-8899-70c2f47a30a8", // Snacks
      },
    ],
  },
  bootleggers: {
    ...defaultConfiguration,
    logo: <BootleggersLogo />,
    theme: bootleggersTheme,
  },
  "cocks-and-cows": {
    ...defaultConfiguration,
    logo: <CocksAndCowsLogo />,
    theme: cocksAndCowsTheme,
  },
  copenhell: {
    ...copenhellConfiguration,
    enableNameInCheckout: true,
    upsales: [
      {
        name: "Suggest snacks if buying anything not snacks",
        categoryIds: [
          "26cede13-0c51-40ef-9f15-3ef3e16c3e84", // Øl
          "a99ac23f-28aa-4580-bdbc-89695a2a4e20", // Cocktail
          "3519acd8-2eab-4589-b056-ccf13af9b0c5", // Cocktail non-alc
          "0c103dd5-2b74-4cbe-a8bb-1016c3a18db7", // Vand
        ],
        categoryToUpsellId: "3b1337c1-40a2-41bb-946c-c4d83beff4c8", // Snacks
      },
    ],
  },
  "copenhell-s2": {
    ...copenhellConfiguration,
    enableNameInCheckout: false,
    logo: <Copenhell26Logo />,
    rebate: undefined,
    upsales: undefined,
  },
};

const LocationConfigurationContext = createContext<LocationConfiguration>(
  configurations["default"],
);

function LocationConfigurationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { locationId } = locationRoute.useParams();

  const locationName = locationNames[locationId] ?? "default";
  const configuration = configurations[locationName];

  useEffect(() => {
    if (configuration.headingTypography === "serif") {
      document.querySelector("body")?.classList.add("heading-serif");
    }
    if (configuration.headingTypography === "cheddar") {
      document.querySelector("body")?.classList.add("heading-cheddar");
    }
  }, [configuration]);

  return (
    <LocationConfigurationContext.Provider value={configuration}>
      {children}
    </LocationConfigurationContext.Provider>
  );
}

function getLocationConfig(locationId: string): LocationConfiguration {
  const locationName = locationNames[locationId] ?? "default";
  return configurations[locationName];
}

export {
  LocationConfigurationContext,
  LocationConfigurationProvider,
  getLocationConfig,
};
